/* =============================================================================
   helpers.js — pure presentation helpers for the Patient Tracker v2 dashboard.
   No API calls, no business logic — formatting + derived display values only.
   ========================================================================== */

const num = (v) => Number(v) || 0;

/** "gangothri thrivedi - MD" -> "GT" ; "Sagar Patil" -> "SP" */
export const getInitials = (name = '') => {
  const clean = String(name).split(' - ')[0].trim();
  if (!clean) return '–';
  const parts = clean.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase() || clean[0].toUpperCase();
};

/** Strip the trailing " - MD" style role suffix to show the clean name. */
export const getDisplayName = (name = '') => String(name).split(' - ')[0].trim() || String(name);

/** Capitalise the first letter of each word: "gangothri thrivedi" -> "Gangothri Thrivedi". */
export const toTitleCase = (s = '') => String(s).toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

/** Extract ONLY a real role/title suffix (after " - "), else '' (no badge shown). */
export const getRoleTitle = (name = '') => {
  const segs = String(name).split(' - ');
  return segs.length > 1 ? segs[segs.length - 1].trim() : '';
};

/** Role badge text: the suffix after " - " (e.g. MD), else the view's role. */
export const getRoleBadge = (name = '', fallback = '') => {
  const segs = String(name).split(' - ');
  const suffix = segs.length > 1 ? segs[segs.length - 1].trim() : '';
  return suffix || fallback || 'Staff';
};

/** Deterministic avatar background from a name (teal-family palette). */
const AVATAR_COLORS = ['#0f766e', '#14b8a6', '#0e7490', '#0d9488', '#15803d', '#0891b2', '#4f46e5', '#b45309'];
export const getAvatarColor = (name = '') => {
  let h = 0;
  const s = String(name);
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

export const formatNumber = (n) => num(n).toLocaleString('en-US');
export const formatPercent = (n, digits = 0) => `${num(n).toFixed(digits)}%`;

/**
 * Compute the dashboard KPIs from the encounter tracker rows (filteredTable1).
 * Mirrors the exact fields/calculations the v1 tables used — no new data.
 */
export const computeKpis = (rows = []) => {
  const r = Array.isArray(rows) ? rows : [];
  const totalPatients = r.reduce((s, x) => s + num(x.nonduplicatePatcount), 0);
  const signed = r.reduce((s, x) => s + num(x.SignedEnc), 0);
  const unsigned = r.reduce((s, x) => s + num(x.UnsignedEnc), 0);
  const totalEncounters = signed + unsigned;
  const totalCPTs = r.reduce((s, x) => s + num(x.CPTCount), 0);
  const totalUnits = r.reduce((s, x) => s + num(x.UnitCount), 0);
  const providerCount = r.length;
  const completionRate = totalEncounters ? (signed / totalEncounters) * 100 : 0;
  const avgUnits = providerCount ? totalUnits / providerCount : 0;
  return {
    totalPatients,
    totalEncounters,
    signed,
    unsigned,
    totalCPTs,
    totalUnits,
    providerCount,
    completionRate,
    avgUnits,
  };
};

/** Status tone for the completion rate KPI. */
export const completionTone = (pct) => {
  if (pct >= 90) return 'success';
  if (pct >= 70) return 'warning';
  return 'error';
};

/** Row-level total encounters for an encounter row. */
export const rowTotalEnc = (row = {}) => num(row.SignedEnc) + num(row.UnsignedEnc);

/* ── location filtering ───────────────────────────────────────────────────
   Providers from GetPracticeProviders are not guaranteed to carry a location.
   These helpers detect a location reference on a provider record under any of
   several common key names / shapes, so the staff list can be scoped by
   location WITHOUT touching the report APIs. If no provider exposes a location,
   `providerHasLocationField` returns false and the UI degrades gracefully. */
const LOCATION_KEYS = [
  'LocationId', 'LocationID', 'LocationIds', 'LocationIDs',
  'Location', 'LocationName', 'LocationNickname',
  'PracticeLocationID', 'PracticeLocationId', 'LocationList', 'Locations',
];

const normLoc = (v) => String(v == null ? '' : v).toLowerCase().trim();

/** All location identifiers/names found on a provider record (lowercased). */
export const getProviderLocationValues = (p) => {
  if (!p) return [];
  const out = [];
  const push = (x) => {
    if (x == null || x === '') return;
    if (typeof x === 'object') {
      out.push(normLoc(x.LocationId ?? x.LocationID ?? x.LocationNickname ?? x.LocationName));
    } else {
      out.push(normLoc(x));
    }
  };
  LOCATION_KEYS.forEach((k) => {
    const v = p[k];
    if (v == null || v === '') return;
    if (Array.isArray(v)) v.forEach(push);
    else if (typeof v === 'string' && v.includes(',')) v.split(',').forEach(push);
    else push(v);
  });
  return out.filter(Boolean);
};

/** True if at least one provider exposes a usable location reference. */
export const providerHasLocationField = (providerData = []) =>
  (providerData || []).some((p) => getProviderLocationValues(p).length > 0);

/** Does a provider belong to the given location object ({LocationId, LocationNickname})? */
export const providerMatchesLocation = (p, loc) => {
  if (!loc) return true; // "All locations"
  const want = [normLoc(loc.LocationId), normLoc(loc.LocationID), normLoc(loc.LocationNickname), normLoc(loc.LocationName)].filter(Boolean);
  const have = getProviderLocationValues(p);
  return have.some((h) => want.includes(h));
};

/** Productivity insights derived from the encounter rows. */
export const computeInsights = (rows = []) => {
  const r = Array.isArray(rows) ? rows : [];
  const k = computeKpis(r);
  let topProvider = null;
  let maxEnc = -1;
  const unsignedOffenders = [];
  r.forEach((x) => {
    const enc = rowTotalEnc(x);
    if (enc > maxEnc) { maxEnc = enc; topProvider = x; }
    if (num(x.UnsignedEnc) > 0) unsignedOffenders.push(x);
  });
  unsignedOffenders.sort((a, b) => num(b.UnsignedEnc) - num(a.UnsignedEnc));
  return {
    kpis: k,
    topProvider,
    topProviderEnc: Math.max(maxEnc, 0),
    unsignedCount: unsignedOffenders.length,
    unsignedTotal: k.unsigned,
    worstUnsigned: unsignedOffenders[0] || null,
  };
};
